import React, {ChangeEvent, FormEvent, ReactElement} from "react";
import {useAppDispatch, useAppSelector} from "../../../lib/redux/hooks";
import {menuManagementActions, menuManagementSelectors} from "./menuManagementSlice";
import {authSelectors} from "../../auth/authSlice";
import {Dialog} from "@headlessui/react";
import {FormattedMessage} from "react-intl";
import {ReactComponent as CloseIcon} from "../../../components/Icons/close.svg";
import {ErrorMessage} from "../../../components/UIComponents/ErrorMessage";
import {Spinner} from "../../../components/UIComponents/Spinner";
import {SecondaryButton} from "../../../components/UIComponents/SecondaryButton";
import {PrimaryButton} from "../../../components/UIComponents/PrimaryButton";
import {Checkbox} from "../../../components/UIComponents/Checkbox";


export function CopyModal(): ReactElement {
    const dispatch = useAppDispatch();
    const menuId = useAppSelector(menuManagementSelectors.copyModalMenuId);
    const menuName = useAppSelector(menuManagementSelectors.copyModalMenuName);
    const copyModalOpen = useAppSelector(menuManagementSelectors.copyModalOpen);
    const copyToUserId = useAppSelector(menuManagementSelectors.copyToUserId);
    const copyToOtherCustomer = useAppSelector(menuManagementSelectors.copyToOtherCustomer);
    const pending = useAppSelector(menuManagementSelectors.copyPending);
    const error = useAppSelector(menuManagementSelectors.copyError);
    const userId = useAppSelector(authSelectors.customer);
    const isSalesUser = useAppSelector(authSelectors.isSalesUser);

    const setCopyToOtherCustomer = (val: boolean) => dispatch(menuManagementActions.setCopyToOtherCustomer(val));
    const closeCopyModal = () => dispatch(menuManagementActions.closeCopyModal());
    const onInputChange = (e: ChangeEvent<HTMLInputElement>) => dispatch(menuManagementActions.setCopyToUserId(e.target.value));
    const submitCopyMenu = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (userId == null) {
            dispatch(menuManagementActions.setCopyError("keine userId vorhanden"));
            return;
        }

        if (menuId == null) {
            dispatch(menuManagementActions.setCopyError("keine menuid vorhanden"));
            return;
        }

        const copyToUser = isSalesUser
            ? (copyToOtherCustomer?copyToUserId:userId)
            : userId;

        if (!copyToUser) {
            dispatch(menuManagementActions.setCopyError("keine userId vorhanden"));
            return;
        }

        return dispatch(menuManagementActions.copyMenu({
            userId: userId,
            fileId: menuId,
            copyToUserId: copyToUser,
            menuName: menuName||""
        }));
    };

    return (
        <Dialog
            open={copyModalOpen}
            onClose={closeCopyModal}
            className="fixed z-modal inset-0 overflow-y-auto"
        >
            <div className="flex items-center justify-center min-h-screen">
                <Dialog.Overlay className="fixed inset-0 bg-text opacity-60"/>

                <div className="relative flex flex-col w-1/3 max-w-sm mx-auto
                                py-6 px-5 bg-white rounded-lg">
                    <div className="flex-none flex items-center">
                        <Dialog.Title className="flex-auto font-barlow text-lg font-semibold">
                            <FormattedMessage id="management.copy.modal.title"/>
                        </Dialog.Title>
                        <button className="flex-none"
                                onClick={closeCopyModal}>
                            <CloseIcon className="inline-block w-5 h-5" />
                        </button>
                    </div>

                    <form className="flex-auto flex flex-col"
                          onSubmit={submitCopyMenu}>
                        <p className="mt-5"><FormattedMessage id="management.copy.modal.label" /></p>


                        <div className="flex mt-5 gap-2"
                             onClick={()=>setCopyToOtherCustomer(false)}
                        >
                            <div className="px-2 h-6">
                                <Checkbox checked={!copyToOtherCustomer}
                                          setChecked={()=>setCopyToOtherCustomer(false)}
                                          />
                            </div>
                            <FormattedMessage id="management.copy.modal.sameCustomer"
                                              values={{customer: userId}}
                            />
                        </div>
                        {isSalesUser&&<div className="flex mt-5 gap-2"
                             onClick={()=>setCopyToOtherCustomer(true)}
                        >
                            <div className="px-2 h-6">
                                <Checkbox checked={copyToOtherCustomer}
                                          setChecked={()=>setCopyToOtherCustomer(true)}
                                />
                            </div>
                            <FormattedMessage id="management.copy.modal.otherCustomer"/>
                        </div>}

                        {copyToOtherCustomer&&<p className="mt-5">
                            <input
                                autoFocus
                                type="text"
                                placeholder={"Kundennummer eingeben"}
                                onChange={onInputChange}
                                value={copyToUserId}
                                disabled={pending}
                                className="w-full border border-text py-2 px-2 mb-2
                                           font-barlow text-text border-[#D8D8D8]"
                            />
                        </p>}

                        {error && (
                            <ErrorMessage>
                                <FormattedMessage id="management.copy.modal.error" values={{message: error}} />
                            </ErrorMessage>
                        )}

                        <div className="flex-none flex gap-2 justify-end items-center mt-5">
                            {pending && (
                                <Spinner className="flex-none w-8 h-8 inline-block" />
                            )}

                            <SecondaryButton onClick={closeCopyModal}>
                                <FormattedMessage id="management.copy.modal.button.cancel"/>
                            </SecondaryButton>

                            <PrimaryButton disabled={pending} type="submit">
                                <FormattedMessage id="management.copy.modal.button.submit"/>
                            </PrimaryButton>
                        </div>
                    </form>
                </div>
            </div>
        </Dialog>
    );
}